import React, { useState } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Spinner,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getAllFlows, deleteFlow } from '../api/flow';
import { useNavigate } from 'react-router-dom';

const SystemFlowList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);

  const fetchFlows = async () => {
    const data = await getAllFlows();
    return data;
  };

  const {
    data: flowData,
    isLoading,
    error,
  } = useQuery('flows', fetchFlows);

  const deleteFlowMutation = useMutation(deleteFlow, {
    onSuccess: () => {
      toast({
        title: 'Flow Deleted',
        description: 'The flow has been successfully deleted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      queryClient.invalidateQueries('flows');
    },
    onError: error => {
      toast({
        title: 'Error Deleting Flow',
        description:
          error.response?.data?.message ||
          'An error occurred while deleting the flow.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const handleDeleteFlow = () => {
    if (selectedFlow) {
      deleteFlowMutation.mutate(selectedFlow.id);
      setIsDeleteModalOpen(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (error) {
    return <Text color="red.500">Error fetching flow data</Text>;
  }

  return (
    <Box w="100%" minH="70vh" p={5}>
      <Flex justifyContent="space-between" alignItems="center" mb={5}>
        <Heading size="md">System Flow List</Heading>
      </Flex>

      <TableContainer>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th textAlign="center">Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {flowData.map(flow => (
              <Tr key={flow.id}>
                <Td>{flow.name}</Td>
                <Td textAlign="center">
                  <Flex justifyContent="center" gap={2}>
                    <Text
                      as="span"
                      cursor="pointer"
                      onClick={() =>
                        window.open(`/flow/view/${flow.id}`, '_blank')
                      }
                    >
                      View
                    </Text>

                    <Text as="span">|</Text>
                    <Text
                      as="span"
                      cursor="pointer"
                      onClick={() =>
                        window.open(`/flow/edit/${flow.id}`, '_blank')
                      }
                    >
                      Edit
                    </Text>
                    <Text as="span">|</Text>
                    <Text
                      as="span"
                      cursor="pointer"
                      color="red.500"
                      onClick={() => {
                        setSelectedFlow(flow);
                        setIsDeleteModalOpen(true);
                      }}
                    >
                      Delete
                    </Text>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalBody>
            {selectedFlow && (
              <Text>
                Are you sure you want to delete{' '}
                <strong>{selectedFlow.name}</strong>?
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              onClick={handleDeleteFlow}
              isLoading={deleteFlowMutation.isLoading}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SystemFlowList;
